import { useMemo } from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { Box, Tooltip } from '@mui/material'
import moment from 'moment'
import { Transfer, TransferReportProps } from 'pages/transfers/[uuid]/report'

import EmptyState from 'components/EmptyState/EmptyState'
import { Table } from 'components/Table'
import { TableNameWrapper, TableNickname } from 'components/Table/Table.styles'

import { formatCpfCnpj } from 'utils'
import { formatMoney } from 'utils/FormatMoney'

import { theme } from 'styles/theme'

const paymentTransform = {
  pix: 'Pix',
  boleto: 'Boleto',
  boleto_pix: 'Boleto/Pix',
  credit_card: 'Cartão de crédito',
  credit_card_recurring: 'Cartão de crédito recorrente',
  credit_card_installments: 'Cartão de crédito parcelado',
  fee_payment: 'Taxa',
  chargeback: 'Chargeback',
  refund: 'Reembolso',
  saas: 'Mensalidade',
  nfse: 'NFS-e',
  transfer: 'Transferência',
  billing_no_payment_report: 'Negativação',
  extra_notification: 'Notificação Extra',
  contract: 'Contrato'
}

export const convertToRows = ({ transfers_items }: Transfer) => {
  const rows = transfers_items?.map((transfer) => {
    const dueDateMoment = moment(transfer.due_date, 'YYYY-MM-DD')
    const dueDate = dueDateMoment.isValid()
      ? dueDateMoment.format('DD/MM/YYYY')
      : ''

    const paidDateMoment = moment(transfer.date_paid, 'YYYY-MM-DD')
    const paidDate = paidDateMoment.isValid()
      ? paidDateMoment.format('DD/MM/YYYY')
      : ''

    const document = transfer?.customer_document
      ? formatCpfCnpj(transfer?.customer_document)
      : ''

    const amountPaid = formatMoney(transfer.gross_amount)
    const fee = formatMoney(transfer.fee)
    const amountLiquid = transfer.amount

    return {
      type: (
        <Tooltip
          enterTouchDelay={0}
          title={transfer.item_type === 'Receivable' ? 'Crédito' : 'Débito'}
        >
          <Icon
            icon={
              transfer.item_type === 'Receivable'
                ? 'faCirclePlus'
                : 'faCircleMinus'
            }
            size={3}
            colorSX={
              transfer.item_type === 'Receivable'
                ? theme.palette.primary[200]
                : theme.palette.error[300]
            }
          />
        </Tooltip>
      ),
      description: {
        component: (
          <TableNameWrapper>
            <p>{transfer.description}</p>
            {transfer.customer_nickname && (
              <TableNickname>{transfer.customer_nickname}</TableNickname>
            )}
          </TableNameWrapper>
        ),
        toString: () => transfer.description || '',
        valueOf: () => transfer.description || ''
      },
      document,
      dueDate,
      paidDate,
      amountPaid,
      fee,
      amountLiquid: formatMoney(amountLiquid),
      paymentType: paymentTransform[transfer.payment_type] || '',
      uuid:
        transfer.item_type === 'Expense'
          ? transfer.item_uuid
          : transfer.billing_uuid,
      clickable: true
    }
  })

  return rows
}

type ReportTransfersTableProps = {
  handleOpenDetailTransfer?: (uuid: string) => void
  handleOpenExpenseDetail?: (uuid: string) => void
} & TransferReportProps

export default function ReportTransfersTable({
  record,
  handleOpenDetailTransfer,
  handleOpenExpenseDetail
}: ReportTransfersTableProps) {
  const uuidToTypeMap = useMemo(() => {
    const map = new Map<string, 'Receivable' | 'Expense'>()
    record.transfers_items.forEach((item) => {
      const uuid =
        item.item_type === 'Expense' ? item.item_uuid : item.billing_uuid
      if (uuid) map.set(uuid, item.item_type)
    })
    return map
  }, [record.transfers_items])

  const handleRowClick = (uuid: string) => {
    if (uuidToTypeMap.get(uuid) === 'Expense') {
      handleOpenExpenseDetail?.(uuid)
    } else {
      handleOpenDetailTransfer?.(uuid)
    }
  }

  return record?.transfers_items?.length ? (
    <Table
      mobileCards
      header={[
        { id: 'type', label: 'Tipo' },
        { id: 'description', label: 'Descrição' },
        { id: 'document', label: 'CPF/CNPJ' },
        { id: 'dueDate', label: 'Data de Vencimento' },
        { id: 'paidDate', label: 'Data de Pagamento' },
        { id: 'amountPaid', label: 'Valor Pago' },
        { id: 'fee', label: 'Tarifa Bancária' },
        { id: 'amountLiquid', label: 'Valor Líquido' },
        { id: 'paymentType', label: 'Método de Pagamento' }
      ]}
      hiddePagination={true}
      rows={convertToRows(record)}
      rowsPerPage={Infinity}
      rowAction={handleRowClick}
      select={{ useSelect: false }}
    />
  ) : (
    <Box display="flex" justifyContent="center">
      <Box maxWidth={{ xs: '100%', md: '60%' }} width="100%" mt="18vh">
        <EmptyState
          title="Nenhuma transferência no período"
          message="Tente ajustar os filtros para visualizar transferências de outros períodos."
        />
      </Box>
    </Box>
  )
}
